import Icon from '@mdi/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@purinanbm/pds-ui';
import { useLocation } from '@reach/router';
import React from 'react';
import { mdiChevronDown } from 'src/assets/icons/mdiIcons';
import { CMS_THEME } from 'src/common/enums';
import { Link } from 'src/components/Link';
import { CmsButton } from 'src/components/button/CmsButton';
import { findClosestParentURL } from 'src/utils/helpers';

interface NavItem {
  id: string | number;
  label: string;
  href: string;
}

interface NavbarProps {
  items: NavItem[];
  theme: CMS_THEME;
  handleGA: (title: string) => void;
}

const SubnavMenuDesktop: React.FC<NavbarProps> = ({ handleGA, items = [], theme }) => {
  const [hiddenStartIndex, setHiddenStartIndex] = React.useState<number>(items.length);
  const [showTrigger, setShowTrigger] = React.useState<boolean>(false);
  const location = useLocation();
  const [activeLink] = React.useState(
    findClosestParentURL(
      location.pathname,
      items.map(item => ({ url: item.href }) as unknown as LinkProps),
    ),
  );
  const dropdownContentId = 'subnav-desktop-dropdown-content';

  const listContainerRef = React.useRef<HTMLUListElement | null>(null);
  const itemRefs = React.useRef<React.RefObject<HTMLLIElement>[]>([]);
  const observerRef = React.useRef<IntersectionObserver | null>(null);
  const visibilityMapRef = React.useRef<Map<number, boolean>>(new Map());

  if (itemRefs.current.length !== items.length) {
    itemRefs.current = Array(items.length)
      .fill(null)
      .map((_, i) => itemRefs.current[i] || React.createRef<HTMLLIElement>());
  }

  const handleIntersection = React.useCallback<IntersectionObserverCallback>(
    entries => {
      let visibilityChanged = false;
      entries.forEach(entry => {
        if (entry.target instanceof HTMLElement && entry.target.dataset.index) {
          const index = parseInt(entry.target.dataset.index, 10);
          const isNowVisible = entry.isIntersecting && entry.intersectionRatio >= 1;
          if (visibilityMapRef.current.get(index) !== isNowVisible) {
            visibilityMapRef.current.set(index, isNowVisible);
            visibilityChanged = true;
          }
        }
      });

      if (!visibilityChanged && visibilityMapRef.current.size > 0) {
        return;
      }

      let firstHiddenIndex = items.length;
      let anyHidden = false;

      for (let i = 0; i < items.length; i++) {
        const isVisible = visibilityMapRef.current.get(i) ?? true;
        if (!isVisible) {
          if (firstHiddenIndex === items.length) {
            firstHiddenIndex = i;
          }
          anyHidden = true;
        }
      }

      setShowTrigger(anyHidden);
      setHiddenStartIndex(firstHiddenIndex);
    },
    [items.length],
  );

  React.useEffect(() => {
    visibilityMapRef.current.clear();

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const rootElement = listContainerRef.current;
    if (!rootElement || items.length === 0) {
      setShowTrigger(false);
      setHiddenStartIndex(items.length);
      return;
    }

    const observerOptions: IntersectionObserverInit = {
      root: rootElement,
      rootMargin: '0px',
      threshold: 1.0,
    };

    observerRef.current = new IntersectionObserver(handleIntersection, observerOptions);

    const currentObserver = observerRef.current;

    itemRefs.current.forEach((itemRef, index) => {
      if (itemRef.current) {
        if (!visibilityMapRef.current.has(index)) {
          visibilityMapRef.current.set(index, true);
        }
        currentObserver.observe(itemRef.current);
      }
    });

    // eslint-disable-next-line consistent-return
    return () => {
      if (currentObserver) {
        currentObserver.disconnect();
      }
    };
  }, [items, handleIntersection]);

  const hiddenItems = items.slice(hiddenStartIndex);

  return (
    <>
      <ul
        ref={listContainerRef}
        className="pds-m-0 pds-flex pds-h-full pds-items-center pds-gap-5 pds-overflow-hidden pds-whitespace-nowrap pds-p-0"
      >
        {items.map((item, index) => {
          const shouldBeHidden = showTrigger && index >= hiddenStartIndex;
          return (
            <li
              key={item.id}
              ref={itemRefs.current[index]}
              data-index={index}
              className="pds-flex-shrink-0"
              tabIndex={shouldBeHidden ? -1 : undefined}
            >
              <Link
                to={item.href}
                theme={theme}
                analytics={false}
                onClick={() => handleGA(item.label)}
                data-active={activeLink === item.href ? 'true' : undefined}
                tabIndex={shouldBeHidden ? -1 : undefined}
                className={`pds-relative pds-rounded pds-text-title-sm pds-font-semibold pds-no-underline [--tw-content:''] after:pds-animated-underline data-[active=true]:after:pds-bottom-[-17px] data-[active=true]:after:pds-h-3 data-[active=true]:after:pds-w-full ${
                  theme === CMS_THEME.LIGHT
                    ? 'data-[active=true]:after:pds-bg-surface-text'
                    : 'data-[active=true]:after:pds-bg-neutral-text'
                }`}
              >
                {item.label}
              </Link>
            </li>
          );
        })}
      </ul>

      {showTrigger && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <CmsButton
              cmsTheme={theme}
              buttonStyle="tonal"
              aria-label="More navigation options"
              aria-controls={dropdownContentId}
              className="pds-group pds-rounded-circle pds-bg-transparent pds-px-3 pds-py-3"
            >
              <Icon
                path={mdiChevronDown}
                size={1.5}
                className="pds-relative pds-top-[1px] pds-h-5 pds-w-5 pds-transition pds-duration-300 group-data-[state=open]:pds-rotate-180"
                aria-hidden="true"
              />
            </CmsButton>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            id={dropdownContentId}
            align="end"
            className={`${
              theme === CMS_THEME.LIGHT ? 'pds-bg-surface' : 'pds-bg-neutral'
            } pds-space-y-4 pds-rounded-none pds-p-4.5 pds-transition pds-duration-300 data-[state=open]:pds-animate-slideDownAndFade [&>*>svg]:pds-hidden`}
          >
            {hiddenItems.map(item => (
              <DropdownMenuItem key={item.id} asChild>
                <Link
                  to={item.href}
                  theme={theme}
                  onClick={() => handleGA(item.label)}
                  className="pds-block pds-w-full pds-px-0 pds-text-title-sm pds-font-semibold"
                >
                  {item.label}
                </Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  );
};

export default SubnavMenuDesktop;
